import { useEffect } from 'react'
import { useLocale } from '../i18n/LocaleContext'
import { SUPPORTED_LOCALES } from '../i18n/routes'
import { absoluteOgImage, absoluteUrl, siteName } from '../config/site'
import { localePath } from '../i18n/routes'
import { plainTextFromMarkdown } from '../utils/markdownText'

function upsertMeta(attr, key, content) {
  if (!content) return
  let el = document.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel, href, extra = {}) {
  if (!href) return
  const selector = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let el = document.querySelector(selector)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    if (extra.hreflang) el.setAttribute('hreflang', extra.hreflang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function clearHreflangLinks() {
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove())
}

/** path senza prefisso lingua, es. /miniatures/foo */
function upsertHreflang(path) {
  clearHreflangLinks()
  for (const loc of SUPPORTED_LOCALES) {
    upsertLink('alternate', absoluteUrl(localePath(loc, path)), {
      hreflang: loc,
    })
  }
  upsertLink('alternate', absoluteUrl(localePath('en', path)), {
    hreflang: 'x-default',
  })
}

/** Testo piano per meta description (no markdown). */
export function toMetaDescription(text, maxLength = 160) {
  return plainTextFromMarkdown(text, maxLength)
}

/**
 * Meta tag, Open Graph, Twitter Card, canonical e hreflang per route SPA.
 * @param {string} path — path senza prefisso lingua (es. /miniatures/slug)
 */
export function usePageSeo({
  title,
  description,
  path = '/',
  imagePath,
  type = 'website',
  noIndex = false,
}) {
  const { locale } = useLocale()
  const canonical = absoluteUrl(localePath(locale, path))
  const image = absoluteOgImage(imagePath)
  const fullTitle = title?.includes(siteName) ? title : `${title} | ${siteName}`
  const ogLocale = locale === 'it' ? 'it_IT' : 'en_GB'

  useEffect(() => {
    document.title = fullTitle

    upsertMeta('name', 'description', description)
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow')

    upsertMeta('property', 'og:type', type)
    upsertMeta('property', 'og:site_name', siteName)
    upsertMeta('property', 'og:title', fullTitle)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:url', canonical)
    upsertMeta('property', 'og:image', image)
    upsertMeta('property', 'og:locale', ogLocale)

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', fullTitle)
    upsertMeta('name', 'twitter:description', description)
    upsertMeta('name', 'twitter:image', image)

    upsertLink('canonical', canonical)
    if (!noIndex) {
      upsertHreflang(path)
    } else {
      clearHreflangLinks()
    }
  }, [fullTitle, description, canonical, image, type, noIndex, path, ogLocale])
}
